import { httpsCallable } from 'firebase/functions';
import { getFbFunctions } from './development';
import {
  doc,
  getDoc,
  getFirestore,
} from 'firebase/firestore';

import { FirebaseError, getApp } from 'firebase/app';
import type { Invitation, RgbColor, TeamMember } from './types';
import { getAuth } from 'firebase/auth';

export async function getTeam(teamId: string) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  const user = auth.currentUser;

  if (!user) {
    this.team = null;
    this.teamData = null;
    return;
  }

  try {
    const teamSnap = await getDoc(doc(db, 'teams', teamId));
    if (teamSnap.exists() && teamSnap.data().members.includes(user.uid)) {
      this.team = teamSnap.id;

      const teamData = teamSnap.data();

      if (teamData.owner == user.uid) teamData.role = 'owner';
      else if (teamData.admins && teamData.admins.includes(user.uid)) teamData.role = 'admin';
      else if (teamData.editors && teamData.editors.includes(user.uid)) teamData.role = 'editor';
      else teamData.role = 'viewer';

      this.teamData = teamData;

      // Check if SSO is enforced for this team
      const ssoDoc = await getDoc(doc(db, 'teams', teamId, 'private', 'sso'));
      if (ssoDoc.exists()) this.teamData.enforceSso = ssoDoc.data().enforceSso;
      else this.teamData.enforceSso = false;
    } else {
      this.team = null;
      this.teamData = null;
    }
  } catch (err) {
    if (err instanceof FirebaseError && err.code == 'permission-denied') {
      // If we failed due to SSO enforcement, see if we can get the sso doc
      try {
        const ssoDoc = await getDoc(doc(db, 'teams', teamId, 'private', 'sso'));
        if (ssoDoc.exists()) {
          this.team = teamId;
          this.teamData = {
            ...this.teamData,
            enforceSso: ssoDoc.data()?.enforceSso ?? false,
            jitEnabled: ssoDoc.data()?.jitEnabled ?? false,
            ssoBypassUsers: [],
          };
        } else {
          console.warn('Failed to fetch team:', err);
          this.team = null;
          this.teamData = null;
        }
      } catch (err2) {
        console.warn('Failed to fetch team:', err2);
        this.team = null;
        this.teamData = null;
      }
    } else {
      console.warn('Failed to fetch team:', err);
      this.team = null;
      this.teamData = null;
    }
  }
}

export async function createTeam(name: string): Promise<string | false> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);

  const createTeamFunction = httpsCallable(functions, 'teams-createTeam');
  try {
    const result = await createTeamFunction({ name }) as { data: string | false };
    this.team = result.data;
    return result.data;
  } catch {
    return false;
  }
}

export async function inviteUser({ email, role }:
{ email: string, role: string }): Promise<{ success: boolean, message: string }> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().admins && docSnap.data().admins.includes(this.user.uid)) {
      const inviteTeamMember = httpsCallable(functions, 'teams-inviteTeamMember');
      const data = {
        email: email,
        role: role,
        teamId: this.team,
      };
      try {
        const result = await inviteTeamMember(data) as { data: { success: boolean, message: string } };

        return result.data;
      } catch {
        return { success: false, message: 'Error inviting user' };
      }
    } else {
      return { success: false, message: 'User not authorized to invite users' };
    }
  } else {
    return { success: false, message: 'User not logged in' };
  }
}

export async function transferTeamOwnership(userId: string): Promise<{ success: boolean, message: string }> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);

  if (this.user && this.team != null && this.userMode == 'team') {
    const transferTeamOwnership = httpsCallable(functions, 'teams-transferTeamOwnership');
    const data = {
      newOwnerId: userId,
      teamId: this.team,
    };
    try {
      const result = await transferTeamOwnership(data) as { data: { success: boolean, message: string } };
      return result.data;
    } catch {
      return { success: false, message: 'Error transferring team ownership' };
    }
  } else {
    return { success: false, message: 'User not logged in' };
  }
}

export async function getTeamMembers(): Promise<TeamMember[]> {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (!this.user) return [];

  const idToken = await auth.currentUser?.getIdToken();

  if (this.team != null && this.userMode == 'team') {
    try {
      const result = await fetch(
        import.meta.env.VITE_API_URL + 'team-members',
        {
          headers: {
            'X-Team-Id': this.team,
            'Authorization': `Bearer ${idToken}`,
          },
        },
      );

      const jsonResult = await result.json() as { success: boolean, result: TeamMember[] };
      if (jsonResult.success) {
        return jsonResult.result;
      } else {
        return [];
      }
    } catch (error) {
      console.warn(error);
      return [];
    }
  } else {
    return [];
  }
}

export async function getTeamRole() {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && this.team != null) {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      let role = 'viewer';

      if (docSnap.data().editors && docSnap.data().editors.includes(this.user.uid)) {
        role = 'editor';
      }

      if (docSnap.data().admins && docSnap.data().admins.includes(this.user.uid)) {
        role = 'admin';
      }

      if (docSnap.data().owner == this.user.uid) {
        role = 'owner';
      }
      return role;
    } else {
      return 'none';
    }
  } else {
    return 'none';
  }
}

export async function verifyInvitation(token: string): Promise<Invitation | false> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);

  const verify = httpsCallable(functions, 'teams-verifyInvitation');
  try {
    const data = {
      token: token,
      origin: this.curDomain,
    };

    const result = await verify(data) as { data: Invitation | false };
    return result.data;
  } catch {
    return false;
  }
}

export async function updateTeamMemberRole(
  { userId, newRole }: { userId: string, newRole: string },
): Promise<boolean> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && this.team != null && this.userMode == 'team') {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().admins && docSnap.data().admins.includes(this.user.uid)) {
      const updateRole = httpsCallable(functions, 'teams-updateTeamMemberRole');
      const data = {
        userId,
        newRole,
        teamId: this.team,
      };
      try {
        const result = await updateRole(data) as { data: boolean };
        return result.data;
      } catch (error) {
        console.warn(error);
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function deleteTeamInvitation(email: string): Promise<boolean> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && this.team != null && this.userMode == 'team') {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().admins && docSnap.data().admins.includes(this.user.uid)) {
      const deleteInvitation = httpsCallable(functions, 'teams-deleteTeamInvitation');
      const data = {
        email,
        teamId: this.team,
      };
      try {
        const result = await deleteInvitation(data) as { data: boolean };
        return result.data;
      } catch {
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function deleteTeamMember(userId: string, teamId?: string): Promise<boolean> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && ((this.team != null && this.userMode == 'team') || teamId != null) ) {
    const docRef = doc(db, 'teams', teamId ?? this.team);
    const docSnap = await getDoc(docRef);
    if (
      docSnap.exists()
      && (docSnap.data().admins && docSnap.data().admins.includes(this.user.uid))
      || userId == this.user.uid
    ) {
      const deleteMember = httpsCallable(functions, 'teams-deleteTeamMember');
      const data = {
        userId,
        teamId: teamId ?? this.team,
      };
      try {
        const result = await deleteMember(data) as { data: boolean };
        return result.data;
      } catch {
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function deleteTeam() {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && this.team != null && this.userMode == 'team') {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().owner == this.user.uid) {
      const deleteTeamFn = httpsCallable(functions, 'teams-deleteTeam');
      try {
        const result = await deleteTeamFn({ teamId: this.team });

        this.team = null;
        this.teamData = null;
        this.userMode = 'user';
        return result.data;
      } catch {
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function updateTeamName(name: string): Promise<boolean> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  if (this.user && this.team != null && this.userMode == 'team') {
    const docRef = doc(db, 'teams', this.team);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().admins && docSnap.data().admins.includes(this.user.uid)) {
      const updateName = httpsCallable(functions, 'teams-updateTeamName');
      const data = {
        name,
        teamId: this.team,
      };
      try {
        const result = await updateName(data) as { data: boolean };
        return result.data;
      } catch {
        return false;
      }
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export async function getUserTeams(): Promise<{ id: string, name: string, customDomain: string | null }[]> {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (!this.user) return [];

  const idToken = await auth.currentUser?.getIdToken();

  try {
    const response = await fetch(`https://api.${import.meta.env.VITE_HOSTNAME}/v1/user/teams`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    });
    const data = await response.json();

    if (data.success) {
      this.userTeams = data.result;
      return data.result;
    } else {
      this.userTeams = [];
      return [];
    }
  } catch (error) {
    console.warn(error);
    this.userTeams = [];
    return [];
  }
}

export async function getUserOwnedTeams(): Promise<{ id: string, name: string, customDomain: string | null }[]> {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (!this.user) return [];

  const idToken = await auth.currentUser?.getIdToken();

  try {
    const response = await fetch(`https://api.${import.meta.env.VITE_HOSTNAME}/v1/user/teams?owned=true`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    });
    const data = await response.json();
    return data.success ? data.result : [];
  } catch (error) {
    console.warn(error);
    return [];
  }
}

export async function switchTeam(teamId: string, refresh = false) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (this.userMode == 'team' && this.team == teamId) return;

  // Check if team is in userTeams
  if (!this.userTeams.some((t: {
    id: string,
    name: string,
    customDomain: string | null,
    color: RgbColor | null
  }) => t.id === teamId)) return;

  // Get team data
  await this.getTeam(teamId);
  if (this.team == null || this.teamData == null) return;

  this.userMode = 'team';

  await this.getColorPreferences();

  const tokenResult = await auth.currentUser?.getIdTokenResult();
  const signInProvider = tokenResult?.signInProvider ?? null;
  /* @ts-expect-error sign_in_attributes */
  const tokenTenantId = tokenResult?.claims.firebase?.sign_in_attributes?.tenant?.id ?? null;

  if (
    (tokenTenantId == null || signInProvider != 'oidc.rackmanage' || tokenTenantId != this.team)
    && this.teamData?.owner !== this.user?.uid
    && this.teamData?.enforceSso
    && !this.teamData?.ssoBypassUsers?.includes(this.user?.uid)
  ) {
    window.location.href = '/app/sso';
  } else {
    if (refresh) {
      window.location.reload();
    }
  }
}

export async function switchToPersonalWorkspace(refresh = false) {
  if (this.userMode == 'user') return;

  this.userMode = 'user';

  await this.getColorPreferences();

  if (this.user) {
    await this.getUserPreferences(this.user.uid);
  }

  if (window.location.pathname == '/app/sso') {
    window.location.href = '/app/dashboard';
  }

  if (refresh) {
    window.location.reload();
  }
}

/** Current workspace as { value, label, color } for the switcher popup. */
export function getCurrentWorkspaceOption(): { value: string; label: string; color: string | null } {
  if (this.userMode === 'user') return { value: 'user', label: 'Personal Workspace', color: null };
  const t = this.userTeams.find((tt: { id: string }) => tt.id === this.team);
  if (!t) return { value: 'user', label: 'Personal Workspace', color: null };
  const color = t.color
    ? '#' + (1 << 24 | t.color.red << 16 | t.color.green << 8 | t.color.blue).toString(16).slice(1)
    : null;
  return { value: t.id, label: t.name, color };
}
